/**
 * Dialogs module
 */
import React from 'react';
import { FaCheck, FaCheckDouble, FaKey, FaLockOpen, FaUser } from "react-icons/fa"; // npm i react-icons #see https://github.com/react-icons/react-icons

import { SpinCog } from '../alerts/spinCog';
import { RedLabel } from '../alerts/redLabel';
import { ResetPasswordMsg } from './resetPasswordMsg';


type PropsType = {
    error;
    onValidAuth: (usr, pass, totp, callback: () => void) => void;
    onSentResetEmail: (email: string) => void;
};

type StateType = {
    canLogin: boolean;
    hasEMail: boolean;
    isLoading: boolean;
    emailSent: boolean;
    showTotp: boolean;
};

/**
 * Represents an authentication window
 * Properties: error, onValidAuth(user, pasw)
 */
export class AuthDialog extends React.Component<PropsType, StateType> {
    private pnResetPasw: HTMLDivElement;
    private txUsername: HTMLInputElement;
    private userName;
    private txPassword: HTMLInputElement;
    private txTotp: HTMLInputElement;
    private btOk: HTMLButtonElement;

    constructor(props) {
        super(props);
        this.state = {
            canLogin: false,
            hasEMail: false,
            isLoading: false,
            emailSent: false,
            showTotp: false
        };
        this.userName = localStorage.getItem('userName');
    }

    /**
     * Render the component
     */
    render() {
        const { error } = this.props;
        const { canLogin, hasEMail, isLoading, emailSent, showTotp } = this.state;
        const blackButton = 'w3-button w3-round-large w3-black w3-hover-green w3-hover-shadow';
        const divStyle = error ? 'w3-border-red shaking' : 'w3-border-blue';

        return (
            <div className="w3-modal-content w3-animate-opacity" style={{ maxWidth: '35em' }}>
                <div className={`w3-card w3-container w3-padding-16 w3-border ${divStyle}`}>
                    <div className="w3-row w3-section">
                        <div className="w3-col" style={{ width: '30px' }}><FaUser size="25px" /></div>
                        <div className="w3-rest">
                            <input type="email" className="w3-input w3-animate-input" autoComplete="username"
                                style={{ height: '34px', width: '60%', maxWidth: '90%' }} placeholder="Username"
                                defaultValue={this.userName} ref={(c) => { this.txUsername = c; }} required
                                onChange={() => {
                                    this.changeValue();
                                    this.validateEMail();
                                }} />
                        </div>
                    </div>
                    <div className="w3-row w3-section">
                        <div className="w3-col" style={{ width: '30px' }}><FaKey size="25px" /></div>
                        <div className="w3-rest">
                            <input type="password" className="w3-input w3-animate-input" autoComplete="current-password"
                                style={{ height: '34px', width: '60%', maxWidth: '90%' }} placeholder="Password" required
                                ref={(c) => { this.txPassword = c; }} onChange={() => this.changeValue()} />
                        </div>
                    </div>
                    {showTotp &&
                        <div className="w3-row w3-section w3-animate-opacity">
                            <div className="w3-col" style={{ width: '35px' }}><FaCheckDouble size="25px" /></div>
                            <div className="w3-rest">
                                <input type="text" className="w3-input w3-animate-input"
                                    style={{ height: '34px', width: '60%', maxWidth: '90%' }} placeholder="OTP for 2-FA"
                                    ref={(c) => { this.txTotp = c; }} required />
                            </div>
                        </div>}

                    {!error && <br /> || <RedLabel error={error} />}
                    <div className="w3-container w3-center">
                        {isLoading ? <SpinCog title="Authenticating..." /> : <>
                            <button className={blackButton} ref={(c) => { this.btOk = c; }} disabled={!canLogin}
                                onClick={() => this.confirmPassword()}>
                                <FaLockOpen /> Login
                            </button><br />
                            <span className="w3-margin-top w3-margin-right w3-tiny">
                                <input type="checkbox" id="cbRememberMe" defaultChecked={!!this.userName}
                                    onChange={(evt) => this.rememberMe(evt.target.checked)} />
                                <label htmlFor="cbRememberMe"> Remember me</label>
                            </span>
                            <a className={`link ${error ? 'w3-small blinking' : 'w3-tiny'}`}
                                onClick={() => this.signinIssue()}>Problem with login?</a>

                            {emailSent ? <div className="w3-margin-top w3-text-green"><b>E-mail sent!</b></div> :
                                <div ref={(c) => { this.pnResetPasw = c; }} className="w3-margin-top w3-left-align w3-hide">
                                    <ResetPasswordMsg />
                                    <div className="w3-margin-top">
                                        {hasEMail &&
                                            <span className={blackButton + " w3-small w3-padding-small"}
                                                title="Send me an e-mail to reset my password"
                                                onClick={() => this.handleSendEmail()}>
                                                <FaCheck /> Send me an e-mail
                                            </span>}
                                    </div>
                                </div>}
                        </>}
                    </div>
                </div>
            </div>
        );
    }

    componentDidMount() {
        // Text events
        this.txUsername.addEventListener("keyup", ({ key }) => {
            if (key === "Enter") this.txPassword.select();
        });
        this.txPassword.addEventListener("keyup", ({ key }) => {
            if (key === "Enter") this.btOk?.click();
        });
        if (this.userName) this.txPassword.focus();
        else this.txUsername.focus();
    }

    promptTotp() {
        this.setState({ showTotp: true }, () => {
            this.txTotp.focus();
            this.txTotp.addEventListener("keyup", ({ key }) => {
                if (key === "Enter") this.btOk?.click();
            });
        });
    }

    /**
     * When login or password fields are changed
     */
    private changeValue() {
        const b = this.txUsername.value.trim().length > 0 && this.txPassword.value.length > 0;
        this.setState({ canLogin: b });
    }

    /**
     * Validate the email by regexp
     */
    private validateEMail() {
        const email = this.txUsername.value.trim();
        const b = email.length > 0 && /^[a-z0-9._%+-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/i.test(email);
        this.setState({ hasEMail: b });
    }

    /**
     * Confirm the user input to authenticate
     */
    private confirmPassword() {
        this.setState({ isLoading: true });
        this.props.onValidAuth(this.txUsername.value.trim(), this.txPassword.value, this.txTotp?.value,
            () => this.setState({ isLoading: false }));
    }

    /**
     * In case of signin issue, let's display a help message with email field
     */
    private signinIssue() {
        const divRPasw = this.pnResetPasw;
        if (divRPasw.className.indexOf('w3-show') === -1) divRPasw.className += ' w3-show';
        else divRPasw.className = divRPasw.className.replace(' w3-show', '');
    }

    /**
     * Send password recovery email with reset link
     */
    private handleSendEmail() {
        const email = this.txUsername.value.trim();
        if (!email) return;

        this.props.onSentResetEmail(email);
        this.setState({ emailSent: true });
    }

    /**
     * Remember me
     * @param {boolean} b the checked state
     */
    private rememberMe(b: boolean) {
        if (b) {
            localStorage.setItem('userName', this.txUsername.value.trim());
        } else { // deactivate it
            localStorage.removeItem('userName');
        }
    }
}
